import "./chunk-PR4QN5HX.js";

// src/setup.ts
import { extendZodWithOpenApi } from "@asteasolutions/zod-to-openapi";
import { z } from "zod";
extendZodWithOpenApi(z);

// src/common/DateRange.ts
import { z as z2 } from "zod";

// src/utils/zod.utils.ts
function conformingSchema() {
  return function(schema) {
    return schema;
  };
}

// src/utils/object.utils.ts
function omitUndefined(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== void 0));
}

// src/common/DateRange.ts
var DateRangeSchema = conformingSchema()(
  z2.object({
    start: z2.string(),
    end: z2.string()
  })
);

// src/common/ExternalReference.ts
import { z as z3 } from "zod";
var ExternalReferenceSchema = conformingSchema()(
  z3.object({
    integration: z3.string().min(1).openapi({ description: "Integration provider identifier", example: "rotaready" }),
    id: z3.string().min(1).openapi({ description: "Entity ID in the external system", example: "12345" }),
    timestamp: z3.string().datetime().optional().openapi({
      description: "ISO 8601 datetime when the reference was established",
      example: "2026-04-02T10:30:00Z"
    })
  })
).openapi("ExternalReference");

// src/common/Identifiable.ts
import { z as z4 } from "zod";
var IdentifiableSchema = conformingSchema()(
  z4.object({
    id: z4.string().openapi({ description: "Unique system identifier", example: "abc-123" }),
    name: z4.string().openapi({ description: "Human-readable display name", example: "Main Restaurant" })
  })
).openapi("Identifiable");

// src/common/IanaTimezone.ts
import { z as z5 } from "zod";
var IanaTimezoneSchema = z5.string().openapi({
  description: "IANA timezone identifier (e.g. Europe/London)",
  example: "Europe/London"
}).refine(
  (tz) => {
    try {
      new Intl.DateTimeFormat("en-GB", { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  },
  { message: "Invalid IANA timezone identifier" }
).openapi("IanaTimezone");

// src/common/JurisdictionCode.ts
import { SystemError } from "@repo/errors";
import { z as z6 } from "zod";
var JurisdictionCodeSchema = conformingSchema()(
  z6.string().regex(
    /^[A-Z]{2}(-[A-Z\d]{1,3})?$/,
    'Invalid jurisdiction code \u2014 expected ISO 3166-1 or ISO 3166-2 format (e.g. "GB", "US-CA")'
  ).openapi({
    description: "ISO 3166-1 or ISO 3166-2 jurisdiction code (e.g. GB, US-CA)",
    example: "GB"
  })
).openapi("JurisdictionCode");
function jurisdictionChain(code) {
  if (!code) {
    throw new SystemError("Expected a non-empty jurisdiction code", {
      friendlyMessage: "Unable to resolve jurisdiction for compliance rule matching",
      debugContext: { code }
    });
  }
  const hyphen = code.indexOf("-");
  if (hyphen === -1) return [code];
  return [code, code.substring(0, hyphen)];
}
function jurisdictionCovers(scope, target) {
  return jurisdictionChain(target).includes(scope);
}

// src/common/Pagination.ts
import { z as z7 } from "zod";
var OffsetPaginationSchema = z7.object({
  limit: z7.number().int().positive().optional(),
  offset: z7.number().int().nonnegative().optional()
});
var PagePaginationSchema = z7.object({
  limit: z7.number().int().positive().optional(),
  page: z7.number().int().positive().optional()
});
var PaginationParamsSchema = z7.union([
  OffsetPaginationSchema,
  PagePaginationSchema
]);

// src/common/RequestActor.ts
import { z as z8 } from "zod";
var UserActorSchema = conformingSchema()(
  z8.object({
    type: z8.literal("user"),
    userId: z8.string().min(1),
    membershipId: z8.string().min(1),
    email: z8.string().email(),
    name: z8.string().min(1),
    source: z8.string().min(1),
    realmId: z8.string().min(1).optional()
  })
);
var SystemActorSchema = conformingSchema()(
  z8.object({
    type: z8.literal("system")
  })
);
var RequestActorSchema = conformingSchema()(
  z8.discriminatedUnion("type", [UserActorSchema, SystemActorSchema])
);

// src/common/Shift.ts
import { z as z10 } from "zod";

// src/common/StaffFilters.ts
import { z as z9 } from "zod";
var StaffFiltersSchema = conformingSchema()(
  z9.object({
    staffGroups: z9.array(IdentifiableSchema).optional().openapi({ description: "Filter by organisational staff groups" }),
    tags: z9.array(IdentifiableSchema).optional().openapi({ description: "Filter by skill or certification tags" }),
    specificEmployees: z9.array(IdentifiableSchema).optional().openapi({ description: "Filter to specific named employees" })
  })
).openapi("StaffFilters");

// src/common/Shift.ts
var ShiftAssignmentMethodSchema = conformingSchema()(
  z10.enum(["open", "specific", "automatic", "existing"])
).openapi("ShiftAssignmentMethod");
var ShiftRestPeriodSchema = conformingSchema()(
  z10.object({
    end: z10.string().datetime({ offset: true, local: false }).openapi({
      description: "When the rest period ends (ISO 8601 datetime with offset)",
      example: "2026-04-02T06:00:00+00:00"
    }),
    duration: z10.string().openapi({ description: "Rest period duration as ISO 8601 duration", example: "PT11H" })
  })
).openapi("ShiftRestPeriod");
var ShiftPaySchema = conformingSchema()(
  z10.object({
    ruleId: z10.string().openapi({
      description: "Pay rule identifier from the payroll system",
      example: "pay-standard-hourly"
    })
  })
).openapi("ShiftPay");
var CoverShiftSiteReferencesSchema = conformingSchema()(
  z10.object({
    origin: IdentifiableSchema.openapi({ description: "Worker's home site" }),
    work: IdentifiableSchema.openapi({ description: "Site where the work is performed" }),
    account: IdentifiableSchema.openapi({ description: "Site charged for this shift" })
  })
).openapi("CoverShiftSiteReferences");
var ShiftSchema = conformingSchema()(
  z10.object({
    id: z10.string().openapi({ description: "Unique shift identifier", example: "shift-2026-04-02-morning" }),
    shiftType: IdentifiableSchema.openapi({
      description: "Role type for this shift (e.g. Server, Chef)"
    }),
    workArea: IdentifiableSchema.openapi({
      description: "Optional work area within the site"
    }).optional(),
    filters: StaffFiltersSchema.openapi({
      description: "Optional staff eligibility filters"
    }).optional(),
    assignmentMethod: ShiftAssignmentMethodSchema.openapi({
      description: "How staff are assigned to this shift"
    }),
    start: z10.string().datetime({ offset: true, local: false }).openapi({
      description: "Shift start as ISO 8601 datetime with offset",
      example: "2026-04-02T09:00:00+01:00"
    }),
    end: z10.string().datetime({ offset: true, local: false }).openapi({
      description: "Shift end as ISO 8601 datetime with offset (must be after start)",
      example: "2026-04-02T17:00:00+01:00"
    }),
    rest: ShiftRestPeriodSchema.openapi({
      description: "Post-shift rest period requirement"
    }).optional(),
    length: z10.string().openapi({
      description: "Gross shift length as an ISO 8601 duration (end \u2212 start)",
      example: "PT8H"
    }),
    pay: ShiftPaySchema.openapi({
      description: "Payroll metadata (populated after assignment)"
    }).optional(),
    site: z10.union([IdentifiableSchema, CoverShiftSiteReferencesSchema]).openapi({
      description: "Site attribution (single site or cover shift references)"
    }),
    metadata: z10.record(z10.string(), z10.unknown()).openapi({
      description: "Optional integration-specific metadata"
    }).optional(),
    committed: ExternalReferenceSchema.openapi({
      description: "External system reference if shift has been committed"
    }).optional()
  }).superRefine((v, ctx) => {
    if (Date.parse(v.end) <= Date.parse(v.start)) {
      ctx.addIssue({
        path: ["end"],
        code: z10.ZodIssueCode.custom,
        message: "End must be after start"
      });
    }
  })
).openapi("Shift");

// src/common/shift-worker-pair.utils.ts
import { z as z14 } from "zod";

// src/workers/Worker.ts
import { z as z11 } from "zod";
var WorkerTypeSchema = conformingSchema()(
  z11.enum(["employee", "outsourced"])
).openapi("WorkerType");
var WorkerSchema = conformingSchema()(
  IdentifiableSchema.extend({
    type: WorkerTypeSchema.openapi({ description: "Employment type" }),
    staffGroup: IdentifiableSchema.optional().openapi({
      description: "Current staff group assignment"
    }),
    tags: z11.array(IdentifiableSchema).optional().openapi({ description: "Skill and certification tags" })
  })
).openapi("Worker");
function toWorker(details) {
  return omitUndefined(WorkerSchema.parse(details));
}

// src/workers/WorkerDetails.ts
import { z as z13 } from "zod";

// src/workers/regional/GB_WorkerAttributes.ts
import { z as z12 } from "zod";
var GB_WorkerAttributesSchema = conformingSchema()(
  z12.object({
    wtdOptOut: z12.boolean().optional().openapi({ description: "UK Working Time Regulations reg. 5 opt-out" })
  })
).openapi("GB_WorkerAttributes");

// src/workers/WorkerDetails.ts
var WorkerRegionalAttributesSchema = conformingSchema()(
  z13.object({
    GB: GB_WorkerAttributesSchema.optional()
  }).strict()
).openapi("WorkerRegionalAttributes");
var WorkerDetailsSchema = conformingSchema()(
  WorkerSchema.extend({
    tags: z13.array(IdentifiableSchema),
    homeSite: IdentifiableSchema,
    isActive: z13.boolean(),
    email: z13.string().email().optional(),
    phone: z13.string().optional(),
    dateOfBirth: z13.string().date("Invalid dateOfBirth \u2014 expected ISO 8601 calendar date (YYYY-MM-DD)").optional(),
    regional: WorkerRegionalAttributesSchema.optional()
  })
);

// src/common/shift-worker-pair.utils.ts
function shiftWorkerPairKey(pair) {
  return `${pair.shift.id}::${pair.worker.id}`;
}
var ShiftWorkerPairSchema = conformingSchema()(
  z14.object({
    shift: ShiftSchema,
    worker: WorkerSchema
  })
);
var ShiftWorkerDetailsPairSchema = conformingSchema()(
  z14.object({
    shift: ShiftSchema,
    worker: WorkerDetailsSchema
  })
);

// src/common/AssignedShift.ts
import { z as z16 } from "zod";

// src/rota-proposals/ShiftBreak.ts
import { z as z15 } from "zod";
var BreakTypeSchema = conformingSchema()(z15.enum(["paid", "unpaid"])).openapi(
  "BreakType"
);
var ShiftBreakSchema = conformingSchema()(
  z15.object({
    type: BreakTypeSchema.openapi({ description: "Whether the break is paid or unpaid" }),
    duration: z15.string().openapi({ description: "Break duration as ISO 8601 duration", example: "PT30M" })
  })
).openapi("ShiftBreak");

// src/common/AssignedShift.ts
var AssignedShiftSchema = ShiftSchema.and(
  z16.object({
    break: ShiftBreakSchema.optional().openapi({
      description: "Rest break resolved for the assigned worker on this shift"
    })
  })
).openapi("AssignedShift");

// src/common/ShiftType.ts
import { z as z17 } from "zod";
var ShiftTypeSchema = conformingSchema()(
  IdentifiableSchema.extend({
    parent: IdentifiableSchema.optional().openapi({
      description: "Direct parent in the shift-type hierarchy, if any"
    }),
    depth: z17.number().int().nonnegative().openapi({
      description: "Depth in the hierarchy (0 for roots)",
      example: 0
    }),
    concatenatedName: z17.string().optional().openapi({
      description: "Full ancestor path as a display string",
      example: "Kitchen / Chef / Sous Chef"
    })
  })
).openapi("ShiftType");

// src/common/Site.ts
import { z as z18 } from "zod";
var DEFAULT_FIRST_DAY_OF_WEEK = 1;
var DEFAULT_DAY_BOUNDARY = 4;
var SiteSettingsSchema = conformingSchema()(
  z18.object({
    firstDayOfWeek: z18.number().int().min(1).max(7).optional().openapi({
      description: "First day of the week (ISO 8601: 1=Monday, 7=Sunday)",
      example: 1
    }),
    dayBoundary: z18.number().int().min(0).max(23).optional().openapi({
      description: "Hour (0\u201323) at which one business day ends and the next begins",
      example: 4
    })
  })
).openapi("SiteSettings");
function siteLocale(site) {
  return {
    timezone: site.timezone,
    dayBoundary: site.settings?.dayBoundary ?? DEFAULT_DAY_BOUNDARY,
    firstDayOfWeek: site.settings?.firstDayOfWeek ?? DEFAULT_FIRST_DAY_OF_WEEK
  };
}
var SiteSchema = conformingSchema()(
  IdentifiableSchema.extend({
    timezone: IanaTimezoneSchema.openapi({
      description: "Site IANA timezone",
      example: "Europe/London"
    }),
    jurisdiction: JurisdictionCodeSchema.openapi({
      description: "Site jurisdiction for compliance rule matching",
      example: "GB"
    }),
    currency: z18.string().optional().openapi({ description: "ISO 4217 currency code", example: "GBP" }),
    settings: SiteSettingsSchema.optional().openapi({
      description: "Site-level scheduling configuration"
    }),
    entityGroup: IdentifiableSchema.optional().openapi({
      description: "Parent grouping for this site (e.g. a region or brand)"
    })
  })
).openapi("Site");

// src/common/TenantContext.ts
import { z as z19 } from "zod";
var TenantContextIntegrationSchema = conformingSchema()(
  z19.object({
    id: z19.string().uuid(),
    tenantId: z19.string().uuid(),
    provider: z19.string().min(1),
    externalRef: z19.string().min(1),
    createdAt: z19.date(),
    updatedAt: z19.date()
  })
);
var TenantContextSchema = conformingSchema()(
  z19.object({
    id: z19.string().uuid(),
    organisationId: z19.string().min(1).optional(),
    applicationId: z19.string().min(1).optional(),
    subscriptionId: z19.string().optional(),
    integration: TenantContextIntegrationSchema.optional().nullable()
  })
);

// src/diagnostics/index.ts
import { z as z29 } from "zod";

// src/diagnostics/standard.diagnostic.ts
import { z as z21 } from "zod";

// src/diagnostics/base.ts
import { z as z20 } from "zod";
var baseDiagnosticFields = {
  source: z20.string().openapi({ description: "Where the diagnostic was raised from" }),
  code: z20.string().openapi({ description: "Stable identifier for this diagnostic's kind" }),
  message: z20.string().openapi({ description: "Human-readable description of the event" }),
  outcome: z20.enum(["success", "error", "warning", "info"]).openapi({ description: "The outcome this diagnostic represents" }),
  timestamp: z20.string().datetime().openapi({ description: "ISO 8601 datetime when the diagnostic was recorded" }),
  metadata: z20.record(z20.string(), z20.unknown()).optional().openapi({ description: "Free-form structured payload" })
};

// src/diagnostics/standard.diagnostic.ts
var StandardDiagnosticSchema = conformingSchema()(
  z21.object({
    ...baseDiagnosticFields,
    type: z21.literal("Diagnostic")
  })
).openapi("StandardDiagnostic");

// src/diagnostics/rule-evaluation.diagnostic.ts
import { z as z26 } from "zod";

// src/diagnostics/rule-evaluation/candidate-rule-evaluation.diagnostic.ts
import { z as z23 } from "zod";

// src/diagnostics/rule-evaluation/rule-evaluation-refs.ts
import { z as z22 } from "zod";
var RuleEvaluationRefsSchema = z22.object({
  ruleId: z22.string(),
  policyEntryId: z22.string().optional(),
  shiftId: z22.string(),
  workerId: z22.string().optional(),
  workerIds: z22.array(z22.string()).optional()
});

// src/diagnostics/rule-evaluation/candidate-rule-evaluation.diagnostic.ts
var candidateRuleEvaluationFindingSchema = z23.object({
  checkId: z23.string(),
  message: z23.string(),
  metadata: z23.record(z23.string(), z23.unknown()).optional(),
  eligible: z23.boolean()
});
var candidateRuleEvaluationResultSchema = z23.object({
  message: z23.string(),
  metadata: z23.record(z23.string(), z23.unknown()).optional(),
  eligible: z23.boolean(),
  findings: z23.array(candidateRuleEvaluationFindingSchema).optional()
});
var CandidateRuleEvaluationDiagnosticSchema = conformingSchema()(
  z23.object({
    ...baseDiagnosticFields,
    type: z23.literal("RuleEvaluation"),
    stage: z23.literal("candidate"),
    result: candidateRuleEvaluationResultSchema.openapi({
      description: "The candidate rule's evaluation output"
    }),
    refs: RuleEvaluationRefsSchema
  })
).openapi("CandidateRuleEvaluationDiagnostic");

// src/diagnostics/rule-evaluation/eligibility-rule-evaluation.diagnostic.ts
import { z as z24 } from "zod";
var eligibilityRuleEvaluationFindingSchema = z24.object({
  checkId: z24.string(),
  message: z24.string(),
  metadata: z24.record(z24.string(), z24.unknown()).optional(),
  eligible: z24.boolean(),
  strict: z24.boolean()
});
var eligibilityRuleEvaluationResultSchema = z24.object({
  message: z24.string(),
  metadata: z24.record(z24.string(), z24.unknown()).optional(),
  eligible: z24.boolean(),
  strict: z24.boolean(),
  findings: z24.array(eligibilityRuleEvaluationFindingSchema).optional()
});
var EligibilityRuleEvaluationDiagnosticSchema = conformingSchema()(
  z24.object({
    ...baseDiagnosticFields,
    type: z24.literal("RuleEvaluation"),
    stage: z24.literal("eligibility"),
    result: eligibilityRuleEvaluationResultSchema.openapi({
      description: "The eligibility rule's evaluation output"
    }),
    refs: RuleEvaluationRefsSchema
  })
).openapi("EligibilityRuleEvaluationDiagnostic");

// src/diagnostics/rule-evaluation/selection-rule-evaluation.diagnostic.ts
import { z as z25 } from "zod";
var selectionRuleEvaluationFindingSchema = z25.object({
  checkId: z25.string(),
  message: z25.string(),
  metadata: z25.record(z25.string(), z25.unknown()).optional(),
  score: z25.number().nullable()
});
var selectionRuleEvaluationResultSchema = z25.object({
  message: z25.string(),
  metadata: z25.record(z25.string(), z25.unknown()).optional(),
  score: z25.number(),
  actual: z25.boolean(),
  findings: z25.array(selectionRuleEvaluationFindingSchema).optional()
});
var SelectionRuleEvaluationDiagnosticSchema = conformingSchema()(
  z25.object({
    ...baseDiagnosticFields,
    type: z25.literal("RuleEvaluation"),
    stage: z25.literal("selection"),
    result: selectionRuleEvaluationResultSchema.openapi({
      description: "The selection rule's evaluation output"
    }),
    refs: RuleEvaluationRefsSchema
  })
).openapi("SelectionRuleEvaluationDiagnostic");

// src/diagnostics/rule-evaluation.diagnostic.ts
var RuleEvaluationDiagnosticSchema = conformingSchema()(
  z26.discriminatedUnion("stage", [
    CandidateRuleEvaluationDiagnosticSchema,
    EligibilityRuleEvaluationDiagnosticSchema,
    SelectionRuleEvaluationDiagnosticSchema
  ])
).openapi("RuleEvaluationDiagnostic");

// src/diagnostics/shift.diagnostic.ts
import { z as z27 } from "zod";
var ShiftDiagnosticSchema = conformingSchema()(
  z27.object({
    ...baseDiagnosticFields,
    type: z27.literal("Shift"),
    refs: z27.object({
      shiftId: z27.string(),
      workerId: z27.string().optional(),
      workerIds: z27.array(z27.string()).optional()
    })
  })
).openapi("ShiftDiagnostic");

// src/diagnostics/policy-entry.diagnostic.ts
import { z as z28 } from "zod";
var PolicyEntryDiagnosticSchema = conformingSchema()(
  z28.object({
    ...baseDiagnosticFields,
    type: z28.literal("PolicyEntry"),
    refs: z28.object({
      policyEntryId: z28.string(),
      ruleId: z28.string()
    })
  })
).openapi("PolicyEntryDiagnostic");

// src/diagnostics/index.ts
var DiagnosticSchema = conformingSchema()(
  z29.discriminatedUnion("type", [
    StandardDiagnosticSchema,
    RuleEvaluationDiagnosticSchema,
    ShiftDiagnosticSchema,
    PolicyEntryDiagnosticSchema
  ])
).openapi("Diagnostic");

// src/policies/EffectivePolicy.ts
import { z as z34 } from "zod";

// src/policies/PolicyScope.ts
import { z as z30 } from "zod";
var PolicyScopeSchema = conformingSchema()(
  z30.object({
    sites: z30.array(z30.string()).optional().openapi({
      description: "Restrict this entry to specific site identifiers",
      example: ["site-abc", "site-xyz"]
    }),
    staffGroups: z30.array(z30.string()).optional().openapi({
      description: "Restrict this entry to specific staff group identifiers",
      example: ["chefs", "waiters"]
    })
  })
).openapi("PolicyScope");

// src/policies/RuleAuthority.ts
import { z as z31 } from "zod";
var RuleAuthoritySchema = conformingSchema()(
  z31.discriminatedUnion("type", [
    z31.object({ type: z31.literal("system") }),
    z31.object({
      type: z31.literal("compliance"),
      jurisdiction: z31.string().openapi({
        description: "The jurisdiction code this rule applies to",
        example: "GB"
      })
    }),
    z31.object({
      type: z31.literal("integration"),
      integration: z31.string().openapi({
        description: "The integration identifier this rule is specific to",
        example: "rotaready"
      })
    }),
    z31.object({
      type: z31.literal("client"),
      clientId: z31.string().openapi({
        description: "The tenant/client identifier this rule was built for",
        example: "tenant-abc"
      })
    })
  ])
).openapi("RuleAuthority");

// src/policies/RuleEnforcement.ts
import { z as z32 } from "zod";
var RuleEnforcementSchema = conformingSchema()(
  z32.enum(["mandatory", "elective"])
).openapi("RuleEnforcement");

// src/policies/SchedulingStage.ts
import { z as z33 } from "zod";
var SchedulingStageSchema = conformingSchema()(
  z33.enum(["candidate", "eligibility", "selection"])
).openapi("SchedulingStage");

// src/policies/EffectivePolicy.ts
var EffectivePolicyEntrySchema = conformingSchema()(
  z34.object({
    rule: z34.object({
      id: z34.string(),
      name: z34.string(),
      stage: SchedulingStageSchema,
      enforcement: RuleEnforcementSchema,
      authority: RuleAuthoritySchema
    }),
    config: z34.unknown().optional(),
    scope: PolicyScopeSchema.optional(),
    specificity: z34.number().int().nonnegative(),
    policyEntryId: z34.string().optional()
  })
).openapi("EffectivePolicyEntry");
var EffectivePolicySchema = conformingSchema()(
  z34.object({
    entries: z34.array(EffectivePolicyEntrySchema)
  })
).openapi("EffectivePolicy");

// src/policies/SchedulingPolicy.ts
import { z as z35 } from "zod";
var PolicyEntrySchema = conformingSchema()(
  z35.object({
    id: z35.string(),
    ruleId: z35.string(),
    stage: SchedulingStageSchema,
    scope: PolicyScopeSchema.optional(),
    config: z35.record(z35.string(), z35.unknown()).optional(),
    enabled: z35.boolean()
  })
).openapi("PolicyEntry");
var SchedulingPolicySchema = conformingSchema()(
  z35.object({
    id: z35.string(),
    tenantId: z35.string(),
    entries: z35.array(PolicyEntrySchema)
  })
).openapi("SchedulingPolicy");

// src/policies/EstablishedPolicyEntry.ts
var EstablishedPolicyEntrySchema = PolicyEntrySchema.omit({
  id: true,
  stage: true
}).openapi("EstablishedPolicyEntry");

// src/policies/policy.mappers.ts
import { SystemError as SystemError2 } from "@repo/errors";
function policyFromSchedulingRuleset(params) {
  const { ruleset, id, tenantId } = params;
  return {
    id,
    tenantId,
    entries: ruleset.rules.map(toPolicyEntry)
  };
}
function schedulingRulesetFromPolicy(policy) {
  const rules = [];
  for (const entry of policy.entries) {
    const rule = toSchedulingRule(entry);
    if (rule !== void 0) {
      rules.push(rule);
    }
  }
  return { rules };
}
function policyFromEffectivePolicy(params) {
  const { effectivePolicy, id, tenantId } = params;
  return {
    id,
    tenantId,
    entries: effectivePolicy.entries.map((entry) => ({
      id: entry.policyEntryId ?? entry.rule.id,
      ruleId: entry.rule.id,
      stage: entry.rule.stage,
      enabled: true,
      ...entry.scope !== void 0 ? { scope: entry.scope } : {},
      ...entry.config !== void 0 ? { config: asConfig(entry.config) } : {}
    }))
  };
}
function schedulingRulesetFromEffectivePolicy(effectivePolicy) {
  return schedulingRulesetFromPolicy(
    policyFromEffectivePolicy({ effectivePolicy, id: "", tenantId: "" })
  );
}
function toPolicyEntry(rule) {
  switch (rule.kind) {
    case "restBreakBetweenShifts":
      return buildEntry("rest-between-shifts", { minimumRestHours: rule.hours });
    case "minimumDaysOff":
      return buildEntry("minimum-days-off", {
        daysOffPerWeek: rule.daysOffPerWeek,
        requireConsecutive: rule.requireConsecutiveDaysOff
      });
    case "splitShifts":
      return buildEntry("split-shifts", {
        splitShiftsEnabled: rule.enabled,
        maxShiftsPerDay: rule.maxShiftsPerDay
      });
    case "maxConsecutiveDays":
      return buildEntry("max-consecutive-days", { maxDays: rule.maxDays });
    default:
      return assertNever(rule);
  }
}
function toSchedulingRule(entry) {
  const config = asConfig(entry.config);
  switch (entry.ruleId) {
    case "rest-between-shifts":
      return {
        kind: "restBreakBetweenShifts",
        enabled: true,
        hours: readNumber(config, "minimumRestHours")
      };
    case "minimum-days-off":
      return {
        kind: "minimumDaysOff",
        enabled: true,
        daysOffPerWeek: readNumber(config, "daysOffPerWeek"),
        requireConsecutiveDaysOff: readBoolean(config, "requireConsecutive")
      };
    case "split-shifts":
      return {
        kind: "splitShifts",
        enabled: readBoolean(config, "splitShiftsEnabled"),
        maxShiftsPerDay: readNumber(config, "maxShiftsPerDay")
      };
    case "max-consecutive-days":
      return {
        kind: "maxConsecutiveDays",
        enabled: true,
        maxDays: readNumber(config, "maxDays")
      };
    default:
      return void 0;
  }
}
function buildEntry(ruleId, config) {
  return {
    id: ruleId,
    ruleId,
    stage: "eligibility",
    enabled: true,
    config
  };
}
function asConfig(config) {
  return typeof config === "object" && config !== null ? config : {};
}
function readNumber(config, field) {
  const value = Number(config[field]);
  if (Number.isNaN(value)) {
    throw new SystemError2("Expected numeric config field", { debugContext: { field, config } });
  }
  return value;
}
function readBoolean(config, field) {
  const value = config[field];
  if (typeof value !== "boolean") {
    throw new SystemError2("Expected boolean config field", { debugContext: { field, config } });
  }
  return value;
}
function assertNever(rule) {
  throw new SystemError2("Unhandled SchedulingRule kind", { debugContext: { rule } });
}

// src/templates/staff-assignment/OpenStaffAssignmentConfig.ts
import { z as z44 } from "zod";

// src/templates/scaling/ScalingMetric.ts
import { z as z36 } from "zod";
var ScalingMetricSchema = conformingSchema()(
  z36.union([z36.literal("sales"), z36.literal("guests")])
).openapi("ScalingMetric");

// src/templates/scaling/Capacity.ts
import { z as z37 } from "zod";
var CapacitySchema = conformingSchema()(
  z37.object({
    sales: z37.number().nonnegative().optional().openapi({
      description: "Sales/takings expected on the day",
      example: 5e3
    }),
    guests: z37.number().nonnegative().optional().openapi({
      description: "Guests/covers expected on the day",
      example: 80
    })
  })
).openapi("Capacity");

// src/templates/scaling/Baseline.ts
import { z as z38 } from "zod";
var ForDayOfWeekSchema = z38.object({
  1: CapacitySchema.optional(),
  2: CapacitySchema.optional(),
  3: CapacitySchema.optional(),
  4: CapacitySchema.optional(),
  5: CapacitySchema.optional(),
  6: CapacitySchema.optional(),
  7: CapacitySchema.optional()
}).strict();
var BaselineSchema = conformingSchema()(
  z38.object({
    default: CapacitySchema.optional().openapi({
      description: "Template-wide baseline capacity for any day not overridden"
    }),
    forDayOfWeek: ForDayOfWeekSchema.optional().openapi({
      description: "Per-day-of-week baseline overrides (1 = Monday, 7 = Sunday)"
    })
  })
).openapi("Baseline");

// src/templates/scaling/Forecast.ts
import { z as z39 } from "zod";
var ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
var ForecastSchema = conformingSchema()(
  z39.object({
    days: z39.record(
      z39.string().regex(ISO_DATE_REGEX, "Forecast keys must be ISO dates (YYYY-MM-DD)"),
      CapacitySchema
    ).openapi({
      description: "Per-day Capacity entries keyed by ISO date"
    })
  })
).openapi("Forecast");

// src/templates/scaling/models/LinearScalingModel.ts
import { z as z40 } from "zod";
var LinearScalingModelSchema = conformingSchema()(
  z40.object({
    type: z40.literal("linear"),
    metric: ScalingMetricSchema.openapi({
      description: "The forecast metric driving this shift"
    })
  })
).openapi("LinearScalingModel");

// src/templates/scaling/models/RatioScalingModel.ts
import { z as z41 } from "zod";
var RatioScalingModelSchema = conformingSchema()(
  z41.object({
    type: z41.literal("ratio"),
    metric: ScalingMetricSchema.openapi({
      description: "The forecast metric driving this shift"
    }),
    unitsPerStaff: z41.number().int().positive().openapi({
      description: "Positive integer units of the metric per additional staff member",
      example: 25
    })
  })
).openapi("RatioScalingModel");

// src/templates/scaling/ScalingModel.ts
import { z as z42 } from "zod";
var ScalingModelSchema = conformingSchema()(
  z42.discriminatedUnion("type", [LinearScalingModelSchema, RatioScalingModelSchema])
).openapi("ScalingModel");

// src/templates/scaling/ScalingConfig.ts
import { z as z43 } from "zod";
var ScalingConfigSchema = conformingSchema()(
  z43.object({
    model: ScalingModelSchema.openapi({
      description: "The scaling strategy to apply"
    }),
    min: z43.number().int().nonnegative().optional().openapi({
      description: "Lower bound on the scaled count"
    }),
    max: z43.number().int().nonnegative().optional().openapi({
      description: "Upper bound on the scaled count"
    })
  }).refine(
    (config) => config.min === void 0 || config.max === void 0 || config.min <= config.max,
    {
      message: "min must be less than or equal to max",
      path: ["min"]
    }
  )
).openapi("ScalingConfig");

// src/templates/staff-assignment/OpenStaffAssignmentConfig.ts
var OpenStaffAssignmentConfigSchema = conformingSchema()(
  z44.object({
    method: z44.literal("open"),
    count: z44.number().openapi({ description: "Number of positions available for self-assignment", example: 3 }),
    filters: StaffFiltersSchema.optional().openapi({
      description: "Optional staff eligibility filters"
    }),
    scaling: ScalingConfigSchema.optional().openapi({
      description: "Optional per-shift scaling configuration"
    })
  })
).openapi("OpenStaffAssignmentConfig");

// src/templates/staff-assignment/SpecificStaffAssignmentConfig.ts
import { z as z45 } from "zod";
var SpecificStaffAssignmentConfigSchema = conformingSchema()(
  z45.object({
    method: z45.literal("specific"),
    employee: IdentifiableSchema.openapi({ description: "The specific employee to assign" })
  })
).openapi("SpecificStaffAssignmentConfig");

// src/templates/staff-assignment/AutomaticStaffAssignmentConfig.ts
import { z as z46 } from "zod";
var AutomaticStaffAssignmentConfigSchema = conformingSchema()(
  z46.object({
    method: z46.literal("automatic"),
    count: z46.number().openapi({ description: "Number of staff positions to fill automatically", example: 2 }),
    filters: StaffFiltersSchema.openapi({
      description: "Filters defining which staff are eligible for automatic assignment"
    }),
    scaling: ScalingConfigSchema.optional().openapi({
      description: "Optional per-shift scaling configuration"
    })
  })
).openapi("AutomaticStaffAssignmentConfig");

// src/templates/staff-assignment/StaffAssignmentConfig.ts
import { z as z47 } from "zod";
var StaffAssignmentConfigSchema = conformingSchema()(
  z47.discriminatedUnion("method", [
    OpenStaffAssignmentConfigSchema,
    SpecificStaffAssignmentConfigSchema,
    AutomaticStaffAssignmentConfigSchema
  ])
).openapi("StaffAssignmentConfig");

// src/templates/ShiftSchedule.ts
import { z as z48 } from "zod";
var ShiftScheduleSchema = conformingSchema()(
  z48.union([
    z48.literal("weekdays").openapi({ description: "Monday to Friday" }),
    z48.literal("weekends").openapi({ description: "Saturday and Sunday" }),
    z48.literal("everyday").openapi({ description: "All days of the week" }),
    z48.array(z48.number().min(1).max(7)).openapi({ description: "Specific days using ISO 8601 numbering (1=Monday, 7=Sunday)" })
  ])
).openapi("ShiftSchedule");

// src/templates/ShiftPattern.ts
import { z as z49 } from "zod";
var ShiftPatternSchema = conformingSchema()(
  z49.object({
    id: z49.string().openapi({ description: "Unique identifier for this shift pattern", example: "sp_abc123" }),
    schedule: ShiftScheduleSchema.openapi({ description: "Days on which this shift occurs" }),
    shiftType: IdentifiableSchema.openapi({
      description: "The role or type of this shift (e.g., Server, Chef)"
    }),
    startTime: z49.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, "Start time must be in HH:mm format (00:00-23:59)").openapi({ description: "Wall-clock start time in HH:mm format", example: "12:00" }),
    endTime: z49.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, "End time must be in HH:mm format (00:00-23:59)").openapi({
      description: "Wall-clock end time in HH:mm format; may be earlier than startTime for overnight shifts",
      example: "22:00"
    }),
    staffing: StaffAssignmentConfigSchema.openapi({
      description: "Configuration for how staff are assigned to this shift"
    }),
    workArea: IdentifiableSchema.openapi({
      description: "Optional work area within the site for shifts generated from this pattern"
    }).optional()
  })
).openapi("ShiftPattern");

// src/templates/scheduling-rules/BaseSchedulingRule.ts
import { z as z50 } from "zod";
var BaseSchedulingRuleSchema = conformingSchema()(
  z50.object({
    kind: z50.string().openapi({ description: "Rule type discriminator" }),
    enabled: z50.boolean().openapi({ description: "Whether this rule is active" })
  })
);

// src/templates/scheduling-rules/RestBreakBetweenShiftsRule.ts
import { z as z51 } from "zod";
var RestBreakBetweenShiftsRuleSchema = conformingSchema()(
  BaseSchedulingRuleSchema.extend({
    kind: z51.literal("restBreakBetweenShifts"),
    hours: z51.number().openapi({
      description: "Minimum rest hours required between consecutive shifts",
      example: 11
    })
  })
).openapi("RestBreakBetweenShiftsRule");

// src/templates/scheduling-rules/SplitShiftsRule.ts
import { z as z52 } from "zod";
var SplitShiftsRuleSchema = conformingSchema()(
  BaseSchedulingRuleSchema.extend({
    kind: z52.literal("splitShifts"),
    maxShiftsPerDay: z52.number().openapi({
      description: "Maximum number of shifts an employee can work per day",
      example: 2
    })
  })
).openapi("SplitShiftsRule");

// src/templates/scheduling-rules/MinimumDaysOffRule.ts
import { z as z53 } from "zod";
var MinimumDaysOffRuleSchema = conformingSchema()(
  BaseSchedulingRuleSchema.extend({
    kind: z53.literal("minimumDaysOff"),
    daysOffPerWeek: z53.number().openapi({ description: "Minimum days off per week", example: 2 }),
    requireConsecutiveDaysOff: z53.boolean().openapi({ description: "Whether days off must be consecutive" })
  })
).openapi("MinimumDaysOffRule");

// src/templates/scheduling-rules/MaxConsecutiveDaysRule.ts
import { z as z54 } from "zod";
var MaxConsecutiveDaysRuleSchema = conformingSchema()(
  BaseSchedulingRuleSchema.extend({
    kind: z54.literal("maxConsecutiveDays"),
    maxDays: z54.number().openapi({
      description: "Maximum number of consecutive days a worker may be scheduled",
      example: 6
    })
  })
).openapi("MaxConsecutiveDaysRule");

// src/templates/scheduling-rules/SchedulingRule.ts
import { z as z55 } from "zod";
var SchedulingRuleSchema = conformingSchema()(
  z55.discriminatedUnion("kind", [
    RestBreakBetweenShiftsRuleSchema,
    SplitShiftsRuleSchema,
    MinimumDaysOffRuleSchema,
    MaxConsecutiveDaysRuleSchema
  ])
).openapi("SchedulingRule");

// src/templates/SchedulingRuleset.ts
import { z as z56 } from "zod";
var SchedulingRulesetSchema = conformingSchema()(
  z56.object({
    rules: z56.array(SchedulingRuleSchema).openapi({ description: "Array of scheduling constraint rules" })
  })
).openapi("SchedulingRuleset");

// src/templates/RotaTemplate.ts
import { z as z57 } from "zod";
var RotaTemplateScopeSchema = conformingSchema()(
  z57.object({
    sites: z57.array(IdentifiableSchema).optional().openapi({
      description: "Sites this template is scoped to; empty or absent means all sites"
    })
  })
).openapi("RotaTemplateScope");
var RotaTemplateSchema = conformingSchema()(
  z57.object({
    id: z57.string().openapi({ description: "Unique identifier for this template", example: "tpl_abc123" }),
    name: z57.string().openapi({
      description: "Human-readable name for the template",
      example: "Weekend Evening Service"
    }),
    description: z57.string().openapi({
      description: "Detailed description of the template's purpose and usage",
      example: "Standard weekend evening rota for front-of-house staff"
    }),
    scope: RotaTemplateScopeSchema.openapi({
      description: "Scope configuration defining which sites can use this template"
    }),
    baseline: BaselineSchema.optional().openapi({
      description: "Optional baseline capacity used by scaling-enabled shifts"
    }),
    shifts: z57.array(ShiftPatternSchema).openapi({ description: "Shift patterns defining the recurring shifts in this template" }),
    schedulingRules: SchedulingRulesetSchema.openapi({
      description: "Rules constraining how shifts can be assigned"
    })
  }).superRefine((template, ctx) => {
    for (const metric of scalingMetricsFor(template.shifts)) {
      if (ALL_DAYS.some((dow) => isMetricResolvable(template.baseline, dow, metric))) continue;
      ctx.addIssue({
        code: z57.ZodIssueCode.custom,
        path: ["baseline"],
        message: `Shifts scale against "${metric}" but the baseline has no "${metric}" figure above 0 for any day; set baseline.default.${metric} or at least one baseline.forDayOfWeek[n].${metric}`
      });
    }
  })
).openapi("RotaTemplate");
var ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];
function scalingMetricsFor(shifts) {
  const metrics = /* @__PURE__ */ new Set();
  for (const { staffing } of shifts) {
    if (staffing.method === "specific" || staffing.scaling === void 0) continue;
    metrics.add(staffing.scaling.model.metric);
  }
  return metrics;
}
function isMetricResolvable(baseline, dow, metric) {
  const value = baseline?.forDayOfWeek?.[dow]?.[metric] ?? baseline?.default?.[metric];
  return value !== void 0 && value > 0;
}

// src/rota-proposals/ProposedShift.ts
import { z as z58 } from "zod";
var ProposedShiftSchema = ShiftSchema.and(
  z58.object({
    proposalId: z58.string().openapi({ description: "Parent proposal this shift belongs to" }),
    shiftPatternId: z58.string().openapi({ description: "Source shift pattern that generated this shift" })
  })
).openapi("ProposedShift");

// src/rota-proposals/ShiftAssignments.ts
import { z as z59 } from "zod";
var ShiftAssignmentsSchema = conformingSchema()(
  z59.object({
    shiftId: z59.string().openapi({ description: "ID of the shift this assignment is for" }),
    shiftPatternId: z59.string().openapi({ description: "Source shift pattern ID" }),
    method: ShiftAssignmentMethodSchema.openapi({
      description: "How this assignment was determined"
    }),
    worker: WorkerSchema.openapi({ description: "The assigned worker" }),
    break: ShiftBreakSchema.nullable().optional().openapi({
      description: "Rest break resolved for the assigned worker; null = computed no break"
    }),
    sharedMetadata: z59.record(z59.string(), z59.unknown()).optional().openapi({
      description: "Combined shared metadata from evaluating rules (e.g. dailyRestFloorHours)"
    })
  })
).openapi("ShiftAssignment");

// src/rota-proposals/RotaDay.ts
import { z as z60 } from "zod";
var RotaDaySchema = conformingSchema()(
  z60.object({
    date: z60.string().regex(/^\d{4}-\d{2}-\d{2}$/).openapi({ description: "Calendar date in YYYY-MM-DD format", example: "2026-04-02" }),
    shifts: z60.array(ProposedShiftSchema).openapi({ description: "All shifts scheduled for this day" }),
    assignments: z60.array(ShiftAssignmentsSchema).openapi({ description: "Worker-to-shift assignments for this day" })
  })
).openapi("RotaDay");

// src/rota-proposals/RotaProposal.ts
import { z as z61 } from "zod";
var RotaGenerationMetaSchema = conformingSchema()(
  z61.object({
    jobId: z61.string().openapi({ description: "Async job ID (if applicable)", example: "job-abc-123" }).optional(),
    at: z61.string().datetime().openapi({
      description: "ISO 8601 datetime when the proposal was generated",
      example: "2026-04-02T10:30:00Z"
    })
  })
).openapi("RotaGenerationMeta");
var ProposalCommitmentStatusSchema = conformingSchema()(
  z61.union([
    z61.literal(false),
    ExternalReferenceSchema,
    z61.object({
      error: z61.unknown().optional()
    })
  ])
).openapi("ProposalCommitmentStatus");
var RotaProposalSchema = conformingSchema()(
  z61.object({
    id: z61.string().openapi({ description: "Unique proposal identifier", example: "prop-2026-04-02-001" }),
    site: SiteSchema.openapi({ description: "The site this proposal is for" }),
    template: IdentifiableSchema.openapi({ description: "Reference to the source template" }),
    generated: RotaGenerationMetaSchema.openapi({
      description: "Metadata about when and how the proposal was generated"
    }),
    committed: ProposalCommitmentStatusSchema.openapi({
      description: "Commitment status \u2014 false if uncommitted, ExternalReference if committed, or error details"
    }),
    days: z61.array(RotaDaySchema).openapi({ description: "The rota days with shifts and assignments" }),
    metadata: z61.record(z61.string(), z61.unknown()).openapi({ description: "Optional integration-specific metadata" }).optional(),
    diagnostics: z61.array(DiagnosticSchema).optional().openapi({ description: "Diagnostics produced during generation" }),
    effectivePolicy: EffectivePolicySchema.nullable().openapi({
      description: "Resolved scheduling policy that produced this proposal \u2014 null pre-assignment"
    })
  })
).openapi("RotaProposal");

// src/integration-ports/IntegrationContext.ts
import { z as z62 } from "zod";
var IntegrationContextMetaSchema = conformingSchema()(
  z62.object({
    tenant: TenantContextSchema,
    requestId: z62.string().uuid(),
    correlationId: z62.string().uuid(),
    actor: RequestActorSchema
  })
);

// src/integration-ports/SiteDirectoryPort.ts
import { z as z63 } from "zod";
var GetSiteByIdParamsSchema = conformingSchema()(
  z63.object({
    siteId: z63.string().min(1)
  })
);
var ListSitesParamsSchema = conformingSchema()(
  z63.object({
    paginate: PaginationParamsSchema.optional()
  })
);
var ListSitesResultSchema = conformingSchema()(
  z63.object({
    sites: z63.array(SiteSchema),
    totalCount: z63.number().int().nonnegative().optional()
  })
);

// src/integration-ports/WorkerDirectoryPort.ts
import { z as z64 } from "zod";
var GetWorkersForSiteParamsSchema = conformingSchema()(
  z64.object({
    siteId: z64.string().min(1),
    includeInactive: z64.boolean().optional(),
    paginate: PaginationParamsSchema.optional(),
    period: DateRangeSchema.optional()
  })
);
var GetWorkersForSiteResultSchema = conformingSchema()(
  z64.object({
    workers: z64.array(WorkerDetailsSchema),
    totalCount: z64.number().int().nonnegative().optional()
  })
);
var GetWorkerByIdParamsSchema = conformingSchema()(
  z64.object({
    workerId: z64.string().min(1)
  })
);
var GetWorkersByIdsParamsSchema = conformingSchema()(
  z64.object({
    workerIds: z64.array(z64.string().min(1)).min(1)
  })
);
var FilterWorkersParamsSchema = conformingSchema()(
  z64.object({
    siteId: z64.string().min(1),
    filters: StaffFiltersSchema,
    includeInactive: z64.boolean().optional()
  })
);

// src/integration-ports/SiteConfigurationPort.ts
import { z as z65 } from "zod";
var ListStaffGroupsForSiteParamsSchema = conformingSchema()(
  z65.object({
    siteId: z65.string().min(1)
  })
);
var ListTagsForSiteParamsSchema = conformingSchema()(
  z65.object({
    siteId: z65.string().min(1)
  })
);
var ListShiftTypesForSiteParamsSchema = conformingSchema()(
  z65.object({
    siteId: z65.string().min(1)
  })
);
var ListStaffGroupsParamsSchema = conformingSchema()(
  z65.object({ siteIds: z65.array(z65.string().min(1)).optional() })
);
var ListTagsParamsSchema = conformingSchema()(
  z65.object({ siteIds: z65.array(z65.string().min(1)).optional() })
);
var ListShiftTypesForSitesParamsSchema = conformingSchema()(
  z65.object({ siteIds: z65.array(z65.string().min(1)).optional() })
);
var ListWorkAreasForSiteParamsSchema = conformingSchema()(
  z65.object({
    siteId: z65.string().min(1)
  })
);
var ListWorkAreasParamsSchema = conformingSchema()(
  z65.object({ siteIds: z65.array(z65.string().min(1)).optional() })
);

// src/integration-ports/ShiftTypeDirectoryPort.ts
import { z as z66 } from "zod";
var ListShiftTypesParamsSchema = conformingSchema()(z66.object({}));
var ListShiftTypesResultSchema = conformingSchema()(
  z66.object({
    shiftTypes: z66.array(ShiftTypeSchema)
  })
);

// src/integration-ports/AvailabilityPort.ts
import { z as z67 } from "zod";
var UnavailabilityCategorySchema = conformingSchema()(
  z67.enum(["absence", "publicHoliday", "workPattern", "notAppointed"])
);
var BlockBoundariesSchema = conformingSchema()(
  z67.enum(["strict", "no-shift-start"])
);
var UnavailabilityBlockSchema = conformingSchema()(
  z67.discriminatedUnion("wholeDay", [
    z67.object({
      category: UnavailabilityCategorySchema,
      countsAsWorked: z67.boolean(),
      boundaries: BlockBoundariesSchema,
      reason: z67.string().optional(),
      wholeDay: z67.literal(true),
      date: z67.string(),
      start: z67.string(),
      end: z67.string()
    }),
    z67.object({
      category: UnavailabilityCategorySchema,
      countsAsWorked: z67.boolean(),
      boundaries: BlockBoundariesSchema,
      reason: z67.string().optional(),
      wholeDay: z67.literal(false),
      start: z67.string(),
      end: z67.string()
    })
  ])
);
var WorkerAvailabilitySchema = conformingSchema()(
  z67.object({
    workerId: z67.string().min(1),
    unavailability: z67.array(UnavailabilityBlockSchema)
  })
);
var GetAvailabilityOfWorkersParamsSchema = conformingSchema()(
  z67.object({
    site: SiteSchema,
    workerIds: z67.array(z67.string().min(1)).min(1),
    periodStart: z67.string(),
    periodEnd: z67.string()
  })
);
var GetAvailabilityOfWorkerParamsSchema = conformingSchema()(
  z67.object({
    site: SiteSchema,
    workerId: z67.string().min(1),
    periodStart: z67.string(),
    periodEnd: z67.string()
  })
);

// src/integration-ports/availability.utils.ts
import { DateTime, Duration, Interval } from "luxon";
function getAvailablePeriods(params) {
  const { period, unavailability } = params;
  if (!period.isValid) return [];
  const clipped = [];
  for (const block of unavailability) {
    const blockInterval = Interval.fromDateTimes(
      DateTime.fromISO(block.start, { setZone: true }),
      DateTime.fromISO(block.end, { setZone: true })
    );
    if (!blockInterval.isValid) continue;
    const overlap = blockInterval.intersection(period);
    if (overlap !== null && overlap.isValid && overlap.length("milliseconds") > 0) {
      clipped.push(overlap);
    }
  }
  const merged = Interval.merge(clipped);
  return period.difference(...merged);
}
function totalAvailableTime(params) {
  return getAvailablePeriods(params).reduce(
    (acc, interval) => acc.plus(interval.toDuration()),
    Duration.fromMillis(0)
  );
}

// src/integration-ports/WorkerSchedulePort.ts
import { z as z68 } from "zod";
var WorkerScheduleAbsenceSchema = conformingSchema()(
  z68.object({
    date: z68.string().date(),
    countsAsWorkingTime: z68.boolean(),
    countsAsRest: z68.boolean(),
    duration: z68.string()
  })
);
var WorkerScheduleSchema = conformingSchema()(
  z68.object({
    workerId: z68.string().min(1),
    shifts: z68.array(AssignedShiftSchema),
    absences: z68.array(WorkerScheduleAbsenceSchema)
  })
);
var GetSchedulesOfWorkersParamsSchema = conformingSchema()(
  z68.object({
    workerIds: z68.array(z68.string().min(1)).min(1),
    period: DateRangeSchema,
    site: SiteSchema
  })
);

// src/integration-ports/WorkerAppointmentsPort.ts
import { z as z69 } from "zod";
var ObligationUnitSchema = conformingSchema()(z69.enum(["hours", "days"]));
var ObligationPeriodSchema = conformingSchema()(
  z69.enum(["week", "month"])
);
var ObligationSchema = conformingSchema()(
  z69.object({
    amount: z69.number(),
    unit: ObligationUnitSchema,
    per: ObligationPeriodSchema
  })
);
var WorkerAppointmentSchema = conformingSchema()(
  z69.object({
    from: z69.string().date(),
    to: z69.string().date().nullable(),
    minimum: ObligationSchema.optional(),
    maximum: ObligationSchema.optional()
  })
);
var WorkerAppointmentsTimelineSchema = conformingSchema()(
  z69.object({
    workerId: z69.string().min(1),
    appointments: z69.array(WorkerAppointmentSchema)
  })
);
var GetAppointmentsForWorkersParamsSchema = conformingSchema()(
  z69.object({
    site: SiteSchema,
    workerIds: z69.array(z69.string().min(1)).min(1),
    period: DateRangeSchema
  })
);
var GetAppointmentsForWorkerParamsSchema = conformingSchema()(
  z69.object({
    site: SiteSchema,
    workerId: z69.string().min(1),
    period: DateRangeSchema
  })
);

// src/integration-ports/RotaProvisioningPort.ts
import { z as z70 } from "zod";
var ValidationIssueSeveritySchema = conformingSchema()(
  z70.enum(["error", "warning", "info"])
).openapi("ValidationIssueSeverity");
var CommitValidationIssueSchema = conformingSchema()(
  z70.object({
    code: z70.string().min(1).openapi({ description: "Issue type identifier", example: "OVERLAPPING_SHIFT" }),
    message: z70.string().min(1).openapi({ description: "Human-readable issue description" }),
    severity: ValidationIssueSeveritySchema.openapi({ description: "Issue severity level" }),
    path: z70.string().openapi({ description: "JSON path to the affected data", example: "days[0].assignments[0]" }).optional(),
    context: z70.record(z70.string(), z70.unknown()).openapi({ description: "Additional contextual details" }).optional()
  })
).openapi("CommitValidationIssue");
var ValidateProposalParamsSchema = conformingSchema()(
  z70.object({
    proposal: RotaProposalSchema,
    strict: z70.boolean().optional(),
    replaceExistingRota: z70.boolean().optional()
  })
);
var ValidateProposalResultSchema = conformingSchema()(
  z70.object({
    isValid: z70.boolean(),
    issues: z70.array(CommitValidationIssueSchema),
    errorCount: z70.number().int().nonnegative(),
    warningCount: z70.number().int().nonnegative()
  })
);
var CommitProposalParamsSchema = conformingSchema()(
  z70.object({
    proposal: RotaProposalSchema,
    commitMessage: z70.string().optional(),
    skipValidation: z70.boolean().optional(),
    replaceExistingRota: z70.boolean().optional()
  })
);
var CommitSuccessResultSchema = conformingSchema()(
  z70.object({
    success: z70.literal(true),
    commitId: z70.string().min(1).openapi({ description: "Unique commit tracking ID", example: "cmt-abc-123" }),
    proposalId: z70.string().min(1).openapi({ description: "ID of the committed proposal" }),
    committedAt: z70.string().datetime().openapi({
      description: "ISO 8601 datetime of the commit",
      example: "2026-04-02T11:00:00Z"
    }),
    assignmentsCreated: z70.number().int().nonnegative().openapi({ description: "Number of new shift assignments created", example: 15 }),
    assignmentsUpdated: z70.number().int().nonnegative().openapi({ description: "Number of existing assignments updated", example: 2 }),
    assignmentsDeleted: z70.number().int().nonnegative().openapi({ description: "Number of assignments removed", example: 0 }),
    viewUrl: z70.string().url().openapi({
      description: "URL to view the committed rota in the source system",
      example: "https://app.rotaready.com/rotas/12345"
    }).optional()
  })
).openapi("CommitSuccessResult");
var CommitFailureResultSchema = conformingSchema()(
  z70.object({
    success: z70.literal(false),
    errorCode: z70.string().min(1).openapi({ description: "Machine-readable failure type", example: "VALIDATION_FAILED" }),
    errorMessage: z70.string().min(1).openapi({ description: "Human-readable error description" }),
    errors: z70.array(z70.custom().openapi({ type: "object", additionalProperties: true })).openapi({ description: "Structured error details" }).optional(),
    validationIssues: z70.array(CommitValidationIssueSchema).openapi({ description: "Specific validation failures that prevented commit" }).optional(),
    isRetryable: z70.boolean().openapi({ description: "Whether the commit can be retried" }),
    retryAfterMs: z70.number().int().positive().openapi({
      description: "Suggested delay in milliseconds before retry",
      example: 5e3
    }).optional()
  })
).openapi("CommitFailureResult");
var CommitResultSchema = conformingSchema()(
  z70.discriminatedUnion("success", [CommitSuccessResultSchema, CommitFailureResultSchema])
).openapi("CommitResult");
var CommitResponseSchema = conformingSchema()(
  z70.object({
    result: CommitResultSchema.openapi({ description: "The outcome of the commit operation" }),
    proposal: RotaProposalSchema.openapi({
      description: "The proposal state after the commit attempt"
    }).optional()
  })
).openapi("CommitResponse");
var RollbackCommitParamsSchema = conformingSchema()(
  z70.object({
    commitId: z70.string().min(1),
    reason: z70.string().optional()
  })
);
var RollbackResultSchema = conformingSchema()(
  z70.object({
    success: z70.boolean(),
    errorMessage: z70.string().optional(),
    isSupported: z70.boolean(),
    rolledBackAt: z70.string().datetime().optional()
  })
);

// src/integration-ports/PolicyPort.ts
import { z as z71 } from "zod";
var GetEstablishedPolicyParamsSchema = conformingSchema()(
  z71.object({
    site: SiteSchema
  })
);
var ComputeBreaksParamsSchema = conformingSchema()(
  z71.object({
    site: SiteSchema,
    shifts: z71.array(ShiftWorkerDetailsPairSchema)
  })
);
export {
  AssignedShiftSchema,
  AutomaticStaffAssignmentConfigSchema,
  BaseSchedulingRuleSchema,
  BaselineSchema,
  BlockBoundariesSchema,
  BreakTypeSchema,
  CandidateRuleEvaluationDiagnosticSchema,
  CapacitySchema,
  CommitFailureResultSchema,
  CommitProposalParamsSchema,
  CommitResponseSchema,
  CommitResultSchema,
  CommitSuccessResultSchema,
  CommitValidationIssueSchema,
  ComputeBreaksParamsSchema,
  CoverShiftSiteReferencesSchema,
  DEFAULT_DAY_BOUNDARY,
  DEFAULT_FIRST_DAY_OF_WEEK,
  DateRangeSchema,
  DiagnosticSchema,
  EffectivePolicyEntrySchema,
  EffectivePolicySchema,
  EligibilityRuleEvaluationDiagnosticSchema,
  EstablishedPolicyEntrySchema,
  ExternalReferenceSchema,
  FilterWorkersParamsSchema,
  ForecastSchema,
  GB_WorkerAttributesSchema,
  GetAppointmentsForWorkerParamsSchema,
  GetAppointmentsForWorkersParamsSchema,
  GetAvailabilityOfWorkerParamsSchema,
  GetAvailabilityOfWorkersParamsSchema,
  GetEstablishedPolicyParamsSchema,
  GetSchedulesOfWorkersParamsSchema,
  GetSiteByIdParamsSchema,
  GetWorkerByIdParamsSchema,
  GetWorkersByIdsParamsSchema,
  GetWorkersForSiteParamsSchema,
  GetWorkersForSiteResultSchema,
  IanaTimezoneSchema,
  IdentifiableSchema,
  IntegrationContextMetaSchema,
  JurisdictionCodeSchema,
  LinearScalingModelSchema,
  ListShiftTypesForSiteParamsSchema,
  ListShiftTypesForSitesParamsSchema,
  ListShiftTypesParamsSchema,
  ListShiftTypesResultSchema,
  ListSitesParamsSchema,
  ListSitesResultSchema,
  ListStaffGroupsForSiteParamsSchema,
  ListStaffGroupsParamsSchema,
  ListTagsForSiteParamsSchema,
  ListTagsParamsSchema,
  ListWorkAreasForSiteParamsSchema,
  ListWorkAreasParamsSchema,
  MaxConsecutiveDaysRuleSchema,
  MinimumDaysOffRuleSchema,
  ObligationPeriodSchema,
  ObligationSchema,
  ObligationUnitSchema,
  OpenStaffAssignmentConfigSchema,
  PaginationParamsSchema,
  PolicyEntryDiagnosticSchema,
  PolicyEntrySchema,
  PolicyScopeSchema,
  ProposalCommitmentStatusSchema,
  ProposedShiftSchema,
  RatioScalingModelSchema,
  RequestActorSchema,
  RestBreakBetweenShiftsRuleSchema,
  RollbackCommitParamsSchema,
  RollbackResultSchema,
  RotaDaySchema,
  RotaGenerationMetaSchema,
  RotaProposalSchema,
  RotaTemplateSchema,
  RotaTemplateScopeSchema,
  RuleAuthoritySchema,
  RuleEnforcementSchema,
  RuleEvaluationDiagnosticSchema,
  RuleEvaluationRefsSchema,
  ScalingConfigSchema,
  ScalingMetricSchema,
  ScalingModelSchema,
  SchedulingPolicySchema,
  SchedulingRuleSchema,
  SchedulingRulesetSchema,
  SchedulingStageSchema,
  SelectionRuleEvaluationDiagnosticSchema,
  ShiftAssignmentMethodSchema,
  ShiftAssignmentsSchema,
  ShiftBreakSchema,
  ShiftDiagnosticSchema,
  ShiftPatternSchema,
  ShiftPaySchema,
  ShiftRestPeriodSchema,
  ShiftScheduleSchema,
  ShiftSchema,
  ShiftTypeSchema,
  ShiftWorkerDetailsPairSchema,
  ShiftWorkerPairSchema,
  SiteSchema,
  SiteSettingsSchema,
  SpecificStaffAssignmentConfigSchema,
  SplitShiftsRuleSchema,
  StaffAssignmentConfigSchema,
  StaffFiltersSchema,
  StandardDiagnosticSchema,
  SystemActorSchema,
  TenantContextIntegrationSchema,
  TenantContextSchema,
  UnavailabilityBlockSchema,
  UnavailabilityCategorySchema,
  UserActorSchema,
  ValidateProposalParamsSchema,
  ValidateProposalResultSchema,
  ValidationIssueSeveritySchema,
  WorkerAppointmentSchema,
  WorkerAppointmentsTimelineSchema,
  WorkerAvailabilitySchema,
  WorkerDetailsSchema,
  WorkerRegionalAttributesSchema,
  WorkerScheduleAbsenceSchema,
  WorkerScheduleSchema,
  WorkerSchema,
  WorkerTypeSchema,
  conformingSchema,
  getAvailablePeriods,
  jurisdictionChain,
  jurisdictionCovers,
  policyFromEffectivePolicy,
  policyFromSchedulingRuleset,
  schedulingRulesetFromEffectivePolicy,
  schedulingRulesetFromPolicy,
  shiftWorkerPairKey,
  siteLocale,
  toWorker,
  totalAvailableTime
};
//# sourceMappingURL=index.js.map